/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useMemo, useState } from 'react';
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { useMediaQuery } from 'react-responsive';

import { useGetApplication } from '~/controllers/wizard';
import { ApplicationPayload } from '~/types/ApplicationPayload';

import { CheckThinIcon } from '~/img';
import { Draft } from '~/types/Draft';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Shareholders } from '~/types/Shareholders';
import { hasAtLeastOneDeclinedField } from '~/utils/softDeclinesFields';
import theme from '~/constants/theme';
import { Space } from '~/components';
import Table from '~/components/Table';
import BeneficialOwnersCardMobile from './BeneficialOwnersCardMobile';
import BeneficialOwnerEdit from './BeneficialOwnerEdit';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';
import BeneficialOwnerAdd from './BeneficialOwnerAdd';
import { isEditButtonDisabled } from './utils';

const BeneficialOwnerTable: FC<Partial<FormItemInjectedProps<Shareholders>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<Shareholders>;
  const shareholdersFormState = { shareholders: value };

  const { t } = useTranslation('fillApplication');

  const applicationQuery = useGetApplication();
  const hasBeneficialOwnersValidationWarnings = hasAtLeastOneDeclinedField(
    applicationQuery.data,
    'shareholders.beneficialOwners[]'
  );

  const individualColumns: ColumnsType<IndividualRowType> = useMemo(
    () => [
      {
        title: t('First name'),
        dataIndex: 'firstName',
        key: 'firstName',
      },
      {
        title: t('Last name'),
        dataIndex: 'lastName',
        key: 'lastName',
      },
      {
        title: t('Passport'),
        dataIndex: 'hasPassport',
        key: 'hasPassport',
        render: (hasPassport: boolean) => {
          if (hasPassport) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Emirates ID'),
        dataIndex: 'hasEmiratesId',
        key: 'hasEmiratesId',
        render: (hasEmiratesId: boolean) => {
          if (hasEmiratesId) return <CheckThinIcon />;
          return null;
        },
      },
      {
        title: t('Shares, %'),
        dataIndex: 'shares',
        key: 'shares',
      },
      {
        title: '',
        key: 'action',
        fixed: 'right',
        render: (_, { onEdit, onDelete }) => (
          <div style={actionsStyle}>
            <Space size={16}>
              {onEdit && (
                <EditButton
                  id="beneficialOwnerTable_EditButton"
                  disabled={isEditButtonDisabled(
                    applicationQuery.data,
                    hasBeneficialOwnersValidationWarnings
                  )}
                  hasValidationWarnings={hasBeneficialOwnersValidationWarnings}
                  onClick={onEdit}
                />
              )}
              <DeleteButton
                id="beneficialOwnerTable_DeleteButton"
                disabled={isEditButtonDisabled(
                  applicationQuery.data,
                  hasBeneficialOwnersValidationWarnings
                )}
                onClick={onDelete}
              />
            </Space>
          </div>
        ),
      },
    ],
    [hasBeneficialOwnersValidationWarnings, applicationQuery.data, t]
  );

  const [editId, setId] = useState('');
  const openEdit = (uid: string) => {
    setId(uid);
  };
  const closeEdit = () => {
    setId('');
  };

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  if (!applicationQuery.data) {
    return null;
  }
  const payload = applicationQuery.data?.payload;

  const individualDataSource = getAllIndividualsData(
    payload,
    shareholdersFormState,
    onChange,
    openEdit
  );

  if (
    isEmpty(shareholdersFormState.shareholders?.businesses || []) &&
    isEmpty(shareholdersFormState.shareholders?.beneficialOwners || [])
  )
    return null;

  return (
    <>
      <BeneficialOwnerEdit uid={editId} onClose={closeEdit} {...props} />
      <Typography.Title level={4}>{t('Beneficial owners').toUpperCase()}</Typography.Title>
      <Typography.Paragraph>
        {t(
          'Because shares are held by a Legal entity as disclosed in the above section, please provide the details of Beneficial owners of the entity.'
        )}
      </Typography.Paragraph>
      <Space size={24} direction="vertical">
        {!isEmpty(individualDataSource) && (
          <>
            {!isTablet ? (
              <Table
                id="beneficialOwnersTable"
                columns={individualColumns}
                dataSource={individualDataSource}
                pagination={pagination}
                rowKey="uid"
              />
            ) : (
              <Space size={12} direction="vertical">
                {individualDataSource.map((item) => (
                  <BeneficialOwnersCardMobile
                    key={item.uid}
                    disableRowActions={isEditButtonDisabled(
                      applicationQuery.data,
                      hasBeneficialOwnersValidationWarnings
                    )}
                    hasBeneficialOwnersValidationWarnings={hasBeneficialOwnersValidationWarnings}
                    {...item}
                  />
                ))}
              </Space>
            )}
          </>
        )}
        {!isEmpty(shareholdersFormState.shareholders?.businesses || []) && (
          <BeneficialOwnerAdd {...props} />
        )}
      </Space>
    </>
  );
};

const pagination = { hideOnSinglePage: true };

type IndividualRowType = {
  uid: string;
  firstName: string | undefined;
  lastName: string | undefined;
  hasPassport: boolean;
  hasEmiratesId: boolean;
  shares: number | undefined;
  onDelete: () => void;
  onEdit?: () => void;
};

const getAllIndividualsData = (
  payload: ApplicationPayload | undefined,
  shareholdersDraft: Draft<'shareholders'> | undefined,
  onFormChange: (value: Shareholders) => void,
  openEdit: (uid: string) => void
): IndividualRowType[] => {
  const shareholders1 = shareholdersDraft?.shareholders as Shareholders;
  return (
    shareholders1?.beneficialOwners?.map((item) => {
      return {
        uid: item?.data?.uid as string,
        firstName: item?.data?.firstName,
        lastName: item?.data?.lastName,
        hasPassport: !isEmpty(item?.data?.passport?.files),
        hasEmiratesId: !isEmpty(item?.data?.emiratesId?.files),
        shares: item?.shares,
        onDelete: () =>
          onFormChange(
            produce(shareholdersDraft, (draft) => {
              const draftShareholders = draft!.shareholders as Shareholders;
              draftShareholders.beneficialOwners = draftShareholders.beneficialOwners!.filter(
                (a) => a?.data?.uid !== item?.data?.uid
              );
            })!.shareholders as Shareholders
          ),
        onEdit: () => item?.data?.uid && openEdit(item.data.uid),
      };
    }) || []
  );
};

const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };

export default BeneficialOwnerTable;
